// Real NDIS-focused blog content for the Resources page.
export const BLOG_POSTS = [
  {
    slug: "ndis-plan-review-preparation",
    cat: "NDIS basics",
    title: "How to prepare for your NDIS plan review (without the stress)",
    excerpt: "A practical checklist for participants and families heading into a plan review — what to bring, what to expect, and how to advocate for what you actually need.",
    image: "https://images.unsplash.com/photo-1553877522-43269d4ea984?crop=entropy&cs=srgb&fm=jpg&w=1600&q=80",
    read: "7 min read",
    date: "2025-11-18",
    body: [
      ["Start with outcomes, not activities",
        "The NDIS plan review is about outcomes — not a list of services. Spend ten minutes writing out what you want to be different in 12 months. Health, housing, social connection, skills, employment, study. The plan flows from there."],
      ["Gather evidence that shows change",
        "Support notes, progress reports, allied health letters, medical updates. What's working, what's not, and what's changed since the last plan. Concrete examples beat general claims every time."],
      ["Know your numbers",
        "Bring spending data. If Core is under-utilised, understand why. If Capacity Building has gaps, flag it. Coordinators and providers can help produce this."],
      ["Bring the right people",
        "A family member, a coordinator, or an allied health professional who knows you well. The planner doesn't know your life — your circle does."],
      ["Ask for what you need — explicitly",
        "If you need more SIL hours, a different SDA category, or new supports, say so plainly. Reference the NDIS Act's definition of 'reasonable and necessary'. Silence rarely gets you more."],
    ],
  },
  {
    slug: "sil-vs-sda-explained",
    cat: "Housing",
    title: "SIL vs SDA vs STA vs MTA — the plain-English version",
    excerpt: "Four acronyms, four very different things. Here's what each of them actually means, who they're for, and how they work together in real life.",
    image: "https://images.unsplash.com/photo-1460317442991-0ec209397118?crop=entropy&cs=srgb&fm=jpg&w=1600&q=80",
    read: "9 min read",
    date: "2025-11-02",
    body: [
      ["SIL — Supported Independent Living",
        "SIL is the support, not the house. It covers the staff, the rostering, the 24/7 capability. You can receive SIL in an SDA home, in a private rental, or in an own-home arrangement."],
      ["SDA — Specialist Disability Accommodation",
        "SDA is the house, not the support. Purpose-built homes for people with extreme functional impairment or very high support needs. Four design categories: Fully Accessible, High Physical Support, Improved Liveability, Robust."],
      ["STA — Short Term Accommodation",
        "Respite and short stays (usually 1–14 nights). Useful for planned breaks, emergencies, or trying a new living arrangement."],
      ["MTA — Medium Term Accommodation",
        "Bridging housing while a long-term home is being set up. Typically up to 90 days, though longer stays can be funded."],
      ["How they fit together",
        "Most participants in long-term housing live in SDA with SIL supports. STA and MTA fill the gaps: respite, discharge from hospital, transitions. A good provider helps you map which you need."],
    ],
  },
  {
    slug: "choosing-a-support-coordinator",
    cat: "Support coordination",
    title: "How to choose (or change) your support coordinator",
    excerpt: "Signs you've outgrown your coordinator, what to look for in a new one, and how to make the handover clean.",
    image: "https://images.unsplash.com/photo-1521791136064-7986c2920216?crop=entropy&cs=srgb&fm=jpg&w=1600&q=80",
    read: "6 min read",
    date: "2025-10-14",
    body: [
      ["Signs to move on",
        "Slow responses, vague plans, unused budget, no progress reports, generic referrals. A good coordinator acts like a project manager for your plan — if yours doesn't, it's time to look around."],
      ["What to look for",
        "Named contact, written expectations, regular check-ins, clean documentation. Ask how many participants they manage — it matters."],
      ["Clean handover",
        "Request a copy of your file. Ask for an introduction call between old and new coordinator. Confirm active providers and budgets."],
      ["Your right to change",
        "You can change your support coordinator at any time. It doesn't require plan reassessment."],
    ],
  },
  {
    slug: "forensic-disability-support",
    cat: "Forensic",
    title: "What is forensic disability support — and who is it for?",
    excerpt: "Forensic overlays are one of the most misunderstood parts of the NDIS. Here's a grounded, respectful explanation.",
    image: "https://images.unsplash.com/photo-1581094794329-c8112a89af12?crop=entropy&cs=srgb&fm=jpg&w=1600&q=80",
    read: "8 min read",
    date: "2025-09-29",
    body: [
      ["The short answer",
        "Forensic disability supports are specialist supports for people with disability who are engaged with the justice system — through custody, community-based orders, or tribunal-ordered arrangements."],
      ["Why it needs specialist providers",
        "Staff training, environment design, behavioural support planning, risk frameworks and stakeholder coordination all differ meaningfully from general SIL."],
      ["What good looks like",
        "Trauma-informed, restorative practice. Active collaboration with legal and clinical stakeholders. Strong positive behaviour support. Clear documentation."],
      ["Myths worth breaking",
        "Forensic placements are not 'warehousing'. Done right, they're restorative — rebuilding a life with structure, respect and real goals."],
    ],
  },
  {
    slug: "first-90-days-in-sil",
    cat: "Moving in",
    title: "The first 90 days in a SIL home — what to expect",
    excerpt: "A grounded guide for participants and families in the first three months of a new Supported Independent Living arrangement.",
    image: "https://images.unsplash.com/photo-1488521787991-ed7bbaae773c?crop=entropy&cs=srgb&fm=jpg&w=1600&q=80",
    read: "6 min read",
    date: "2025-09-12",
    body: [
      ["Week 1 — settling, not performing",
        "Keep expectations low. New environments take time. Focus on routines, sleep, food, and meeting the team."],
      ["Weeks 2–4 — rhythm",
        "Roster patterns settle. Household culture starts to form. First small outings. Small wins matter most here."],
      ["Days 30–60 — goals",
        "Your support plan gets reviewed with fresh data. Time to refine goals, update risk and behaviour support plans, and check in with family."],
      ["Days 60–90 — rhythm matures",
        "First quarterly review. Is the house the right fit? Is the team right? Honest conversations now save years of drift later."],
    ],
  },
  {
    slug: "family-carer-respite-guide",
    cat: "Families",
    title: "A family-carer's guide to respite — without the guilt",
    excerpt: "Respite isn't a nice-to-have. It's how sustainable care happens. Here's how to plan it without the guilt.",
    image: "https://images.unsplash.com/photo-1529156069898-49953e39b3ac?crop=entropy&cs=srgb&fm=jpg&w=1600&q=80",
    read: "5 min read",
    date: "2025-08-25",
    body: [
      ["Respite is funded for a reason",
        "The NDIS funds STA specifically because sustainable informal care needs breaks. Using it isn't an admission of failure — it's good practice."],
      ["Plan ahead, not in crisis",
        "Crisis respite is hard to source. Book standing blocks: school holidays, anniversaries, one weekend a quarter."],
      ["Know what 'good' looks like",
        "Consistent workers, an environment your family member enjoys, clear communication. If respite feels stressful, it's not respite."],
      ["Protect the handover",
        "Written routines, medication list, emergency contacts. A half-hour handover saves half a week of stress."],
    ],
  },
];

export const BLOG_BY_SLUG = Object.fromEntries(BLOG_POSTS.map((p) => [p.slug, p]));
